import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { deleteBorder } from "@dashboard/shared/styles";
import { CommandPayload } from "@data/commands";
import { DeviceMdmCommandStatusIcon, MdmCommand, MdmCommandStatus } from "@devices/types/commands";
import { Box, Collapse, Typography, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import TimeService from "@services/timeService/timeService";
import { DangerOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { ExpandToggleButton } from "@shared/ExpandToggleButton";
import { MinimalTableDetailsRow } from "@shared/table/CollapsibleRow";
import { MinimalTableCell, MinimalTableRow } from "@shared/table/MinimalTableComponents";
import { Tooltip } from "@shared/Tooltip";

import { getCommandName, getCommandStatusLabel } from "../../shared/CommandTab/CommandTab.utils";

const useStyles = makeStyles()((theme) => ({
    commandPropertyValue: {
        display: "inline-block",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    icons: {
        marginTop: theme.spacing(1),
        marginRight: theme.spacing(2),
        fontSize: "14px",
        color: grey[500],
    },
    collapsingRowContent: {
        padding: theme.spacing(2),
        width: "100%",
        maxWidth: "100%",
        minHeight: 36,
        textAlign: "left",
    },
    commandPayload: {
        margin: 0,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
    },
    collapsePropertyTitle: {
        fontWeight: "bold",
    },
    collapseBtn: {
        "&:focus": {
            outline: "none",
        },
    },
    borderBottomVisible: {
        borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
    },
    overflow: {
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    customCollapse: {
        "& .MuiCollapse-wrapper": {
            maxWidth: "650px",
        },
    },
    btnBox: {
        display: "flex",
        justifyContent: "flex-end",
    },
}));

interface GroupCommandItemProps {
    command: MdmCommand;
    index: number;
    isDeleting: boolean;
    isCreating: boolean;
    createCommand: (json: CommandPayload, commandId: number | null) => Promise<void>;
    deleteCommand: (command: MdmCommand) => Promise<void>;
}

const GroupCommandItem = ({ command, index, isDeleting, isCreating, createCommand, deleteCommand }: GroupCommandItemProps) => {
    const { t } = useTranslation(["sidepanels"]);
    const { classes } = useStyles();
    const { id, payload, created, creator, statuses } = command;
    const [open, setOpen] = useState(-1);
    const theme = useTheme();

    const commandJson: CommandPayload = typeof payload === "string" ? JSON.parse(payload) : payload;
    const commandPayload = JSON.stringify(commandJson, null, 2);

    const handleSubmitCommand = async () => {
        await createCommand(commandJson, id);
    };

    const handleDeleteCommand = async () => {
        await deleteCommand(command);
    };

    const createdDate = TimeService.getLocalDate(created, "ISO", "dateTime");

    const renderStatuses = () => {
        const statusCounts: Record<MdmCommandStatus, number> = {
            unknown: 0,
            pending: 0,
            running: 0,
            successful: 0,
            failed: 0,
            received: 0,
            ignored: 0,
            queued: 0,
        };

        if (statuses) {
            Object.entries(statuses).forEach(([status, count]) => {
                if (status in statusCounts && typeof count === "number") {
                    statusCounts[status as MdmCommandStatus] = count;
                }
            });
        }

        const statusElements = Object.entries(statusCounts).map(([status, count]) => {
            if (count > 0) {
                const iconClassName = DeviceMdmCommandStatusIcon[status as MdmCommandStatus] ?? "fa-solid fa-question";
                const statusLabel = getCommandStatusLabel(status as MdmCommandStatus);
                const devicesLabel = t("sidepanels:groupPanel.commandsTab.device", { count });

                return (
                    <Tooltip placement="top" key={status} title={`${statusLabel} (${devicesLabel})`}>
                        <i key={status} className={clsx(classes.icons, iconClassName)} />
                    </Tooltip>
                );
            } else {
                return null;
            }
        });

        return <>{statusElements}</>;
    };

    return (
        <>
            <MinimalTableRow key={id}>
                <MinimalTableCell align="center" style={deleteBorder(index, open)}>
                    <ExpandToggleButton
                        expanded={open === index}
                        onClick={() => setOpen(open === index ? -1 : index)}
                        className={classes.collapseBtn}
                    />
                </MinimalTableCell>
                <MinimalTableCell style={deleteBorder(index, open)}>
                    <Tooltip title={TimeService.getLocalDate(created, "ISO", "dateTime")}>
                        <Typography variant="body1">{TimeService.getLocalDate(created, "ISO", "date")}</Typography>
                    </Tooltip>
                </MinimalTableCell>
                <MinimalTableCell align="center" style={deleteBorder(index, open)}>
                    {renderStatuses()}
                </MinimalTableCell>
                <MinimalTableCell align="left" style={deleteBorder(index, open)} className={clsx(classes.overflow)}>
                    <Typography variant="body1">{getCommandName(commandJson)}</Typography>
                </MinimalTableCell>
                <MinimalTableCell align="right" style={deleteBorder(index, open)}>
                    <Box className={classes.btnBox}>
                        <SecondaryButton
                            tooltipProps={{ title: t("sidepanels:groupPanel.tooltip.resendCommand") }}
                            onClick={handleSubmitCommand}
                            disabled={isCreating}
                            loading={isCreating}
                        >
                            <i className="fa-solid fa-arrows-rotate" />
                        </SecondaryButton>
                        <DangerOutlinedButton
                            style={{ marginLeft: theme.spacing(1) }}
                            tooltipProps={{ title: t("sidepanels:groupPanel.tooltip.deleteCommand") }}
                            onClick={handleDeleteCommand}
                            disabled={command.status !== "pending" || isDeleting}
                            loading={isDeleting}
                        >
                            <i className="fa-solid fa-trash" />
                        </DangerOutlinedButton>
                    </Box>
                </MinimalTableCell>
            </MinimalTableRow>
            <MinimalTableDetailsRow
                colSpan={5}
                className={open === index ? classes.borderBottomVisible : ""}
                cellClassName={classes.customCollapse}
                cellStyle={{ padding: "0 16px" }}
            >
                <Collapse in={open === index} timeout="auto" unmountOnExit>
                    <div className={classes.collapsingRowContent}>
                        <Typography variant="body1" className={classes.collapsePropertyTitle}>
                            {t("sidepanels:groupPanel.commandsTab.details")}
                        </Typography>
                        <Typography variant="body1">
                            {t("sidepanels:groupPanel.commandsTab.creator", { value: creator })}
                        </Typography>
                        <Typography variant="body1">
                            {t("sidepanels:groupPanel.commandsTab.created", { value: createdDate })}
                        </Typography>
                        <br />
                        <Typography variant="body1" className={classes.collapsePropertyTitle}>
                            {t("sidepanels:groupPanel.commandsTab.content")}
                        </Typography>
                        <Typography component="pre" className={classes.commandPayload}>
                            {commandPayload}
                        </Typography>
                    </div>
                </Collapse>
            </MinimalTableDetailsRow>
        </>
    );
};

export default GroupCommandItem;
